import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import MetricsRow from '../shared/MetricsRow'
import { ConsignmentBadge } from '../shared/ConsignmentBadge.jsx'
import { payoutStateChip, saleStatusChip, attentionLabel } from '../../utils/consignmentStates'
import DealLink from '../shared/DealLink.jsx'

// Views are applied client-side over the one whole-book response — the book is
// small and the heavy joins/math are already done server-side, so filtering is
// just row visibility. 'Live & sold' is the union in_stock OR sold, which the
// server has no single filter for. An unsold row (spec 20261016) is never
// in_stock, so it drops out of 'Live & sold' and shows under 'All'.
const FILTERS = [
  { key: 'live_sold', label: 'Live & sold', test: r => r.in_stock || !!r.sale },
  { key: 'in_stock', label: 'In stock', test: r => r.in_stock },
  { key: 'sold', label: 'Sold', test: r => !!r.sale },
  { key: 'attention', label: 'Needs attention', test: r => r.attention?.length > 0 },
  { key: 'all', label: 'All', test: () => true },
]

const LOAD_ERROR = 'Could not load the consignment book — Prism unreachable'

// Deductions can be adjusted only while the payout is pending and not under
// refund review (a reviewed payout is voided, not amended). legacy_unlinked is
// excluded: Prism refuses to recompute a payout with no linked sale rows.
const ADJUSTABLE_STATES = ['awaiting_shipment', 'in_return_window', 'eligible']

function Consignments() {
  const [data, setData] = useState(null)
  // Land on live and sold together — the units actually in play.
  const [filter, setFilter] = useState('live_sold')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionModal, setActionModal] = useState(null) // { kind: 'adjust' | 'void' | 'unsold', row }

  // One fetch of the whole book; the filter chips narrow it client-side.
  useEffect(() => { loadBook() }, [])

  async function loadBook() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/consignments?filter=all')
      setData(response)
    } catch (e) {
      console.error('Failed to load consignment book:', e)
      setError(LOAD_ERROR)
    } finally {
      setLoading(false)
    }
  }

  const activeFilter = FILTERS.find(f => f.key === filter) || FILTERS[0]
  const rows = (data?.rows || []).filter(activeFilter.test)
  const metrics = data ? [
    { label: 'Units', value: data.total, color: 'purple' },
    { label: 'In Stock', value: data.in_stock_count, color: 'blue' },
    { label: 'Sold', value: data.sold_count, color: 'green' },
    { label: 'Stock Value', value: (data.in_stock_value_cents ?? 0) / 100, color: 'blue', isCurrency: true },
    { label: 'Pending Payouts', value: (data.pending_payout_cents ?? 0) / 100, color: 'purple', isCurrency: true },
  ] : []

  return (
    <div className="pageBody" style={{ paddingTop: 24 }}>
      {data && <MetricsRow metrics={metrics} />}

      <div className="card" style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontWeight: 600, marginRight: 4 }}>Show:</span>
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="card" style={{ marginTop: 'var(--space-4)', padding: 'var(--space-6)', textAlign: 'center' }}>
          <div style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 12 }}>{error}</div>
          <button className="btn btn-primary" onClick={() => loadBook()}>Retry</button>
        </div>
      )}

      {!error && (
        <div className="card" style={{ marginTop: 'var(--space-4)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--muted)' }}>
              Loading consignment book…
            </div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--muted)' }}>
              <div style={{ fontSize: 48, marginBottom: 'var(--space-3)' }}>🏷️</div>
              <h3>No consigned units{filter !== 'all' ? ' match this filter' : ''}</h3>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Listed</th>
                    <th style={{ textAlign: 'right' }} title="The seller's share of the sale price">Split %</th>
                    <th style={{ textAlign: 'right' }} title="The floor as a share of the listed price — we can only go this low with the customer's permission">Floor %</th>
                    <th style={{ textAlign: 'right' }}>Current price</th>
                    <th style={{ textAlign: 'right' }} title="The lowest we can sell at in pounds — only with the customer's permission (listed price × floor %)">Floor price</th>
                    <th>Stock</th>
                    <th>Sale</th>
                    <th>Payout</th>
                    <th style={{ width: 110 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <ConsignmentRow key={row.item_id} row={row} onAction={setActionModal} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {actionModal?.kind === 'adjust' && (
        <AdjustDeductionsModal
          row={actionModal.row}
          onClose={() => setActionModal(null)}
          onSaved={() => { setActionModal(null); loadBook() }}
        />
      )}
      {actionModal?.kind === 'unsold' && (
        <MarkUnsoldModal
          row={actionModal.row}
          onClose={() => setActionModal(null)}
          onSaved={() => { setActionModal(null); loadBook() }}
        />
      )}
      {actionModal?.kind === 'void' && (
        <VoidPayoutModal
          row={actionModal.row}
          onClose={() => setActionModal(null)}
          onSaved={() => { setActionModal(null); loadBook() }}
        />
      )}
    </div>
  )
}

function ConsignmentRow({ row, onAction }) {
  const saleChip = saleStatusChip(row.sale)
  const payoutChip = payoutStateChip(row.payout_state, row.days_remaining)
  const canAdjust = row.quote_id && ADJUSTABLE_STATES.includes(row.payout_state)
  const canVoid = row.payment_id && row.payout_state === 'refund_review'
  // Mark as Unsold (spec 20261016): a live unit with no sale goes back to its
  // owner. A draft is not on sale, so there is nothing to take off.
  const canMarkUnsold = row.in_stock && !row.sale && !row.unsold && !row.is_draft
  return (
    <tr>
      <td className="col-mobile-primary">
        <div style={{ fontWeight: 600 }}>{row.title || 'Untitled unit'}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          {row.item_id} <ConsignmentBadge splitPct={row.split_pct} />
        </div>
        {/* The originating quote code (e.g. TI-DA2029FE) — the original deal id
            operators identify a consignment by. Absent on legacy unlinked units. */}
        {row.reference_code && (
          <div style={{ fontSize: 12, color: 'var(--muted)' }}><DealLink dealId={row.quote_id || row.reference_code} reference={row.reference_code} /></div>
        )}
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>Shopify: {row.shopify_product_id}</div>
        {row.attention?.length > 0 && (
          <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {row.attention.map(flag => (
              <span key={flag} className="badge badge-error badge-sm">{attentionLabel(flag)}</span>
            ))}
          </div>
        )}
      </td>
      <td>{row.listed_at ? formatDate(row.listed_at) : '—'}</td>
      {/* D16: split is THE SELLER'S SHARE, rendered raw — never 100 − split. */}
      <td style={{ textAlign: 'right' }}>{row.split_pct != null ? `${row.split_pct}%` : '—'}</td>
      <td style={{ textAlign: 'right' }}>{row.floor_pct != null ? `${row.floor_pct}% of list` : '—'}</td>
      <td style={{ textAlign: 'right' }}>
        {row.current_price_cents != null ? formatCurrency(row.current_price_cents / 100) : '—'}
      </td>
      <td style={{ textAlign: 'right' }}>
        {row.floor_price_cents != null ? formatCurrency(row.floor_price_cents / 100) : '—'}
      </td>
      <td>
        {/* Draft takes precedence over In stock: a draft unit is still in the
            warehouse, but the salient state is that it is not yet live (and why
            the active-only inventory counts exclude it). */}
        {row.unsold ? (
          <div>
            <span className="badge badge-neutral" title="Marked unsold: the unit goes back to its owner">Unsold</span>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{formatDate(row.unsold.marked_at)}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{row.unsold.reason}</div>
          </div>
        ) : row.is_draft ? (
          <span
            className="badge badge-warning"
            title="Listed as a draft on Shopify — still in the warehouse, but not yet live, so it is excluded from the active-only inventory counts"
          >
            Draft
          </span>
        ) : row.in_stock ? (
          <span className="badge badge-success">In stock</span>
        ) : (
          <span className="badge badge-neutral">Out of stock</span>
        )}
      </td>
      <td>
        {row.sale ? (
          <div>
            <div style={{ fontSize: 12 }}>
              {row.sale.channel} · {formatDate(row.sale.sold_at)} · {formatCurrency(row.sale.sale_price_cents / 100)}
            </div>
            {saleChip && <span className={`badge ${saleChip.badgeClass}`}>{saleChip.label}</span>}
          </div>
        ) : '—'}
      </td>
      <td>
        {payoutChip ? (
          <div>
            <span className={`badge ${payoutChip.badgeClass}`}>{payoutChip.label}</span>
            {row.payout_amount_cents != null && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                {formatCurrency(row.payout_amount_cents / 100)}{row.deductions_cents > 0 ? ' net' : ''}
              </div>
            )}
            {/* The adjustment subtracts from the payout — show the reduction,
                not just the result: gross = net + deductions. */}
            {row.deductions_cents > 0 && (
              <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>
                − {formatCurrency(row.deductions_cents / 100)} deducted
              </div>
            )}
            {row.deductions_cents > 0 && row.payout_amount_cents != null && (
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                from {formatCurrency((row.payout_amount_cents + row.deductions_cents) / 100)}
              </div>
            )}
          </div>
        ) : '—'}
      </td>
      <td>
        {canAdjust && (
          <button className="btn btn-sm btn-secondary" onClick={() => onAction({ kind: 'adjust', row })}>
            Adjust
          </button>
        )}
        {canMarkUnsold && (
          <button className="btn btn-sm btn-danger" onClick={() => onAction({ kind: 'unsold', row })}>
            Mark as unsold
          </button>
        )}
        {canVoid && (
          <button className="btn btn-sm btn-danger" onClick={() => onAction({ kind: 'void', row })}>
            Confirm void
          </button>
        )}
      </td>
    </tr>
  )
}

function AdjustDeductionsModal({ row, onClose, onSaved }) {
  const currentDeductions = row.deductions_cents ?? 0
  // Payout before any deduction: the current net plus whatever is deducted now.
  const grossCents = (row.payout_amount_cents ?? 0) + currentDeductions

  // Prefill with the current deduction so the operator edits from the real
  // state (and can zero it to remove the adjustment).
  const [deductions, setDeductions] = useState(currentDeductions ? String(currentDeductions / 100) : '')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState(null)

  const parsed = deductions === '' ? 0 : parseFloat(deductions)
  const newDeductionsCents = Number.isFinite(parsed) ? Math.round(parsed * 100) : NaN
  const newPayoutCents = grossCents - newDeductionsCents
  const overGross = Number.isFinite(newDeductionsCents) && newDeductionsCents > grossCents
  const validAmount = Number.isFinite(newDeductionsCents) && newDeductionsCents >= 0 && !overGross
  const removing = currentDeductions > 0 && newDeductionsCents === 0

  // Any change to the payout must be auditable — the note is always required.
  const canSave = !saving && note.trim() && validAmount

  async function save() {
    setSaving(true)
    setModalError(null)
    try {
      await api.patch(`/api/consignments/${row.quote_id}/payout`, {
        deductions_cents: newDeductionsCents,
        note: note.trim(),
      })
      onSaved()
    } catch (e) {
      setModalError(e.message)
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Adjust deductions</h3>
        </div>
        <div className="modal-body-scroll" style={{ padding: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
            {row.title || row.item_id} — payout before deductions is {formatCurrency(grossCents / 100)}
            {currentDeductions > 0 && <> (currently {formatCurrency(currentDeductions / 100)} deducted)</>}.
          </div>
          <label htmlFor="adjust-deductions" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
            Deductions (£)
          </label>
          <input
            id="adjust-deductions"
            type="number"
            min="0"
            step="0.01"
            className="form-input"
            value={deductions}
            onChange={e => setDeductions(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          {currentDeductions > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              style={{ marginBottom: 12 }}
              onClick={() => setDeductions('0')}
            >
              Remove deduction (set to £0)
            </button>
          )}
          {/* Show the resulting payout as the operator types — no guessing. */}
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            {overGross ? (
              <div style={{ color: '#b91c1c', fontWeight: 600 }}>
                Deductions exceed the {formatCurrency(grossCents / 100)} payout
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 600 }}>New payout: {formatCurrency(newPayoutCents / 100)}</div>
                {newDeductionsCents > 0 && (
                  <div style={{ color: 'var(--muted)' }}>
                    {formatCurrency(grossCents / 100)} less {formatCurrency(newDeductionsCents / 100)} deductions
                  </div>
                )}
                {removing && <div style={{ color: 'var(--muted)' }}>Deduction removed</div>}
              </>
            )}
          </div>
          <label htmlFor="adjust-note" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
            Note
          </label>
          <textarea
            id="adjust-note"
            className="form-input"
            rows={2}
            placeholder="Why? e.g. repair — replaced pot, or removed — entered in error"
            value={note}
            onChange={e => setNote(e.target.value)}
            style={note.trim() ? {} : { borderColor: '#b45309', background: '#fffbeb' }}
          />
          {modalError && (
            <div style={{ marginTop: 12, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{modalError}</div>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave}>
            {saving ? 'Saving…' : removing ? 'Remove deduction' : 'Save adjustment'}
          </button>
        </div>
      </div>
    </div>
  )
}

function VoidPayoutModal({ row, onClose, onSaved }) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState(null)

  async function confirmVoid() {
    setSaving(true)
    setModalError(null)
    try {
      await api.post(`/api/consignments/payments/${row.payment_id}/fail`, {
        reason: reason.trim(),
      })
      onSaved()
    } catch (e) {
      setModalError(e.message)
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Void payout — refund review</h3>
        </div>
        <div className="modal-body-scroll" style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            The linked order for <strong>{row.title || row.item_id}</strong> shows a refund.
            Voiding cancels the pending
            {row.payout_amount_cents != null ? ` ${formatCurrency(row.payout_amount_cents / 100)}` : ''} payout;
            items detach and a resale is detected as a new sale. This does not move money.
          </div>
          <label htmlFor="void-reason" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
            Reason
          </label>
          <textarea
            id="void-reason"
            className="form-input"
            rows={2}
            placeholder="e.g. full refund confirmed on the Shopify order"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
          {modalError && (
            <div style={{ marginTop: 12, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{modalError}</div>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-danger" onClick={confirmVoid} disabled={saving || !reason.trim()}>
            {saving ? 'Voiding…' : 'Void payout'}
          </button>
        </div>
      </div>
    </div>
  )
}

function MarkUnsoldModal({ row, onClose, onSaved }) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState(null)

  async function confirmUnsold() {
    setSaving(true)
    setModalError(null)
    try {
      await api.post(`/api/consignments/items/${row.item_id}/unsold`, { reason: reason.trim() })
      onSaved()
    } catch (e) {
      setModalError(e.message)
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Mark as unsold</h3>
        </div>
        <div className="modal-body-scroll" style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <strong>{row.title || row.item_id}</strong> goes back to its owner. Its stock goes to 0 on
            Shopify, and eBay and Reverb follow at their next sync. This cannot be undone.
          </div>
          <label htmlFor="unsold-reason" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
            Reason
          </label>
          <textarea
            id="unsold-reason"
            className="form-input"
            rows={2}
            placeholder="e.g. customer asked for it back"
            maxLength={500}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
          {modalError && (
            <div style={{ marginTop: 12, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{modalError}</div>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-danger" onClick={confirmUnsold} disabled={saving || !reason.trim()}>
            {saving ? 'Saving…' : 'Mark as unsold'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Consignments
